import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tr, Td } from 'react-super-responsive-table';
import { AiFillEye } from 'react-icons/ai';
import { FaTrash } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { tsXLXS } from 'ts-xlsx-export';

import CashflowOptions from '../../components/CashflowOptions';
import CashflowTableInlineFilters, { CashflowInlineFilterState } from '../../components/CashflowTableInlineFilters';
import ImagesList from '../../components/ImagesList';
import DeleteRecurrenceModal from '../../components/DeleteRecurrenceModal';
import AuthContext from '../../contexts/AuthContext';
import { CashflowContext } from '../../contexts/CashflowContext';
import UIContext from '../../contexts/UIContext';
import { ICashflowFormLocationState } from '../../Interfaces/ICashflowFormLocationState';
import { ICashflowImages } from '../../Interfaces/ICashflowImages';
import { ICashflowPaginateData } from '../../Interfaces/ICashflowPaginate';
import { canViewUploadCashflow } from '../../policies/canViewUploadCashflow';
import { cashflowService } from '../../services/cashflow';
import { currencyFormat } from '../../helpers/currencyFormat';
import { fixSpelling } from '../../helpers/fixSpelling';
import { getDate } from '../../helpers/dates';
 
import './styles.css';

type CloseData = { type: "close" | "manual" | "upload", classification?: "pagamento" | "recebimento" }

const cashflowOptionsInitialValue = { open: false, defaultStep: 1 };
const modalInitialState = {
  open: false,
  defaultSelected: 0,
  recurrenceId: ''
};

const Cashflow: React.FC = () => {

  const {
    handleOpenImagesList,
    paginateData,
    getInvoices,
    getParameters,
    filter,
    getCashflowData,
    setFilters,
    hasFilter,
    data
  } = useContext(CashflowContext)
  const { userType, token, consumerData } = useContext(AuthContext);
  const { setComponentUIState } = useContext(UIContext);

  const [cashflowOptions, setCashflowOptions] = useState<{ open: boolean, defaultStep: number }>({ ...cashflowOptionsInitialValue });
  const [images, setImages] = useState<ICashflowImages[]>([]);
  const [deleteRecurrenceModal, setDeleteRecurrenceModal] = useState({ ...modalInitialState });
  const [exporting, setExporting] = useState<boolean>(false);

  const navigate = useNavigate();

  useEffect(() => {
    getInvoices();
    getParameters();
    getImagesData();
  }, [])

  function handleNavigateForm(classification?: "pagamento" | "recebimento") {
    navigate("form", { state: { data: {}, action: "add", classification } as ICashflowFormLocationState });
  }

  const handleExportToExcel = async () => {
    if (exporting) return;

    setExporting(true);
    setComponentUIState({
      component: "Toast",
      componentState: { open: true, type: "info", message: "Gerando planilha, aguarde..." }
    });

    try {
      const { status, data: { data: exportData } } = await cashflowService.exportToExcel(
        filter,
        token,
        userType === "Consumer" && consumerData?.id ? { clientId: consumerData.id } : undefined
      );

      if (status !== 200) {
        throw new Error(`Falha ao exportar dados, status code: ${status}`);
      }

      if (!exportData || exportData.length === 0) {
        setComponentUIState({
          component: "Toast",
          componentState: { open: true, type: "warning", message: "Nenhum dado encontrado para exportação." }
        });
        setExporting(false);
        return;
      }

      const formattedData = exportData.map(row => ({
        client: row.client.companyName,
        mes: row.month,
        ano: row.year,
        data: row.date,
        conta: row.invoice,
        condicao: row.classification,
        origem: row.origin,
        historico: row.history,
        valor: row.value,
        status: row.status
      }));

      await tsXLXS()
        .exportAsExcelFile(formattedData)
        .saveAsExcelFile(`fluxo_de_caixa_export`);

      setComponentUIState({
        component: "Toast",
        componentState: { open: true, type: "success", message: "Exportação iniciada com sucesso!" }
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Falha ao exportar dados.";
      setComponentUIState({
        component: "Toast",
        componentState: { open: true, type: "error", message }
      });
    } finally {
      setExporting(false);
    }
  }

  const handleEditCashflowForm = (tableData: typeof data[number]) => {
    navigate("form", { state: { data: { ...tableData }, action: "edit" } as ICashflowFormLocationState });
  }

  const handleOpenCashflowOptions = (step: number) => {
    setCashflowOptions({ open: true, defaultStep: step });
  }

  const handleCloseCashflowOptions = (closeData: CloseData) => {
    if (closeData.type === "close") return setCashflowOptions(cashflowOptionsInitialValue);
    if (closeData.type === "manual") {
      handleNavigateForm(closeData.classification)
      return setCashflowOptions(cashflowOptionsInitialValue);
    }

    return setCashflowOptions(cashflowOptionsInitialValue);
  }

  const handleNewCashFlow = () => {
    const canView = canViewUploadCashflow(userType, consumerData);
    if (!canView) {
      return handleOpenCashflowOptions(2);
    }

    return handleOpenCashflowOptions(1);
  }

  const inlineFilters = useMemo<CashflowInlineFilterState>(() => {
    const valueFilter = typeof filter.value === 'string' ? filter.value : undefined;
    const numericFilter = filter.valor !== undefined && filter.valor !== null ? String(filter.valor) : undefined;

    return {
      client: filter.client || filter.clientName || '',
      mes: filter.mes || filter.month || '',
      ano: filter.ano || filter.year || '',
      data: filter.data || (filter.dateStart && filter.dateStart === filter.dateEnd ? filter.dateStart : ''),
      conta: filter.conta || filter.invoice || '',
      condicao: filter.condicao || filter.classification || '',
      origem: filter.origem || filter.origin || '',
      historico: filter.historico || filter.history || '',
      valor: valueFilter !== undefined && valueFilter !== '' ? valueFilter : (numericFilter || ''),
      status: filter.status || '',
    };
  }, [filter]);

  const handleInlineFilterChange = useCallback((nextFilters: CashflowInlineFilterState) => {
    const updatedFilter = { ...filter };

    const normalize = (value?: string) => {
      if (!value) return undefined;
      const trimmed = value.trim();
      return trimmed.length ? trimmed : undefined;
    };

    const normalizedClient = normalize(nextFilters.client);
    updatedFilter.client = normalizedClient;
    updatedFilter.clientName = normalizedClient;

    const normalizedConta = normalize(nextFilters.conta);
    updatedFilter.conta = normalizedConta;
    updatedFilter.invoice = normalizedConta;

    const normalizedCondicao = normalize(nextFilters.condicao);
    updatedFilter.condicao = normalizedCondicao;
    updatedFilter.classification = normalizedCondicao;

    const normalizedOrigem = normalize(nextFilters.origem);
    updatedFilter.origem = normalizedOrigem;
    updatedFilter.origin = normalizedOrigem;

    const normalizedHistorico = normalize(nextFilters.historico);
    updatedFilter.historico = normalizedHistorico;
    updatedFilter.history = normalizedHistorico;

    const normalizedValor = normalize(nextFilters.valor);
    if (normalizedValor !== undefined) {
      const numericValue = Number(normalizedValor.replace(',', '.'));
      if (Number.isFinite(numericValue)) {
        updatedFilter.valor = numericValue;
      } else {
        updatedFilter.valor = normalizedValor;
      }
      updatedFilter.value = normalizedValor;
    } else {
      updatedFilter.valor = undefined;
      updatedFilter.value = undefined;
    }

    const normalizedStatus = normalize(nextFilters.status);
    updatedFilter.status = normalizedStatus;

    const normalizedMes = normalize(nextFilters.mes);
    updatedFilter.mes = normalizedMes;
    updatedFilter.month = normalizedMes;

    const normalizedAno = normalize(nextFilters.ano);
    updatedFilter.ano = normalizedAno;
    updatedFilter.year = normalizedAno;

    const normalizedData = normalize(nextFilters.data);
    if (normalizedData) {
      updatedFilter.data = normalizedData;
      updatedFilter.dateStart = normalizedData;
      updatedFilter.dateEnd = normalizedData;
    } else {
      updatedFilter.data = undefined;
      updatedFilter.dateStart = undefined;
      updatedFilter.dateEnd = undefined;
    }

    setFilters(updatedFilter);
  }, [filter, setFilters]);

  const closeDeleteRecurrenceModal = () => {
    setDeleteRecurrenceModal({ ...modalInitialState });
  }

  const handleDelete = (id: number, recurrenceId: string | null) => {
    if (recurrenceId) return setDeleteRecurrenceModal({ open: true, defaultSelected: id, recurrenceId: recurrenceId });
    Swal.fire({
      title: '<strong>Confirmação</strong>',
      icon: 'warning',
      html: 'Tem certeza que deseja deletar esse lançamento ?',
      showCloseButton: true,
      showCancelButton: true,
      focusConfirm: false,
      confirmButtonText:
        '<i class="fa fa-thumbs-up"></i> Sim',
      confirmButtonAriaLabel: 'Thumbs up, great!',
      cancelButtonText:
        '<i class="fa fa-thumbs-down"></i> Não',
      cancelButtonAriaLabel: 'Thumbs down'
    }).then((result) => {
      if (result.isConfirmed) {
        deleteCashflow(id);
      }
    })
  }

  const handleDeleteRecurrence = (deleteIds: number[]) => {
    Swal.fire({
      title: '<strong>Confirmação</strong>',
      icon: 'warning',
      html: `Tem certeza que deseja deletar ${deleteIds.length} lançamento(s) ?`,
      showCloseButton: true,
      showCancelButton: true,
      focusConfirm: false,
      confirmButtonText:
        '<i class="fa fa-thumbs-up"></i> Sim',
      confirmButtonAriaLabel: 'Thumbs up, great!',
      cancelButtonText:
        '<i class="fa fa-thumbs-down"></i> Não',
      cancelButtonAriaLabel: 'Thumbs down'
    }).then((result) => {
      if (result.isConfirmed) {
        deleteCashflow(deleteIds, true);
      }
    })
  }

  async function deleteCashflow(id: number | number[], isRecurrence: boolean = false) {
    try {
      const { status } = await cashflowService.deleteCashflow(token, id, isRecurrence);
      if (status === 200) {
        setDeleteRecurrenceModal({ ...modalInitialState })
        getCashflowData("Initial", paginateData, hasFilter, true)
        setComponentUIState({ component: "Toast", componentState: { open: true, type: "success", message: "Lançamento(s) deletado com sucesso!" } });
      } else {
        setComponentUIState({ component: "Toast", componentState: { open: true, type: "error", message: "Falha ao apagar lançamento(s)!" } });
      }
    } catch (error) {
      setComponentUIState({ component: "Toast", componentState: { open: true, type: "error", message: "Falha ao apagar lançamento(s)!" } });
    }
  }

  async function getImagesData() {
    if (userType === "Administrator" || userType === "Analyst") {
      try {
        const { status, data: { images } } = await cashflowService.getImages(token);
        if (status === 200) {
          setImages(images);
        }
      } catch (error) { }
    }

    if (userType === "Consumer") {
      try {
        const { status, data: { images } } = await cashflowService.getConsumerImages(token, consumerData?.id || 0);
        if (status === 200) {
          setImages(images);
        }
      } catch (error) { }
    }
  }

  const handleNextPage = () => {
    if (paginateData.currentPage === paginateData.lastPage) return;
    const paginateContent = {
      ...paginateData,
      skip: paginateData.skip + 10,
      currentPage: paginateData.currentPage + 1
    }

    getCashflowData("Next", paginateContent)
  }

  const handlePrevPage = () => {
    if (paginateData.currentPage === 1) return;

    const paginateContent: ICashflowPaginateData = {
      ...paginateData,
      skip: paginateData.skip - 10,
      currentPage: paginateData.currentPage - 1
    }

    getCashflowData("Prev", paginateContent)
  }

  return (
    <div className="cashflow-container">
      <div className="cashflow-header">
        <button className="header-btn" onClick={handleNewCashFlow} >
          Novo Lançamento
        </button>
        <button className="header-btn" onClick={() => handleOpenImagesList(true)}>
          Imagens Importadas
        </button>
        <button
          className="header-btn export-btn"
          onClick={handleExportToExcel}
          disabled={exporting}
        >
          {exporting ? (
            <>
              Exportando
              <span className="header-spinner" />
            </>
          ) : (
            'Exportar Excel'
          )}
        </button>
      </div>

      <div className="cashflow-body">
        <div className="table-buttons">
          <button onClick={handlePrevPage} className={paginateData.currentPage === 1 ? "disabled-btn" : ""}>Anterior</button>
          <span>Página {paginateData.lastPage ? paginateData.currentPage : 0} de {paginateData.lastPage}</span>
          <button onClick={handleNextPage} className={paginateData.currentPage === paginateData.lastPage ? "disabled-btn" : ""}>Próxima</button>
        </div>
        <div className="table-content">
          <CashflowTableInlineFilters
            filters={inlineFilters}
            onFilterChange={handleInlineFilterChange}
          >
            {data && data.length > 0 ? data.map((row, index) => (
              <Tr key={row.id || index}>
                <Td>{row.client.companyName}</Td>
                <Td>{row.month}</Td>
                <Td>{row.year}</Td>
                <Td>{getDate.brasilFormat(row.date)}</Td>
                <Td>{row.invoice}</Td>
                <Td>{fixSpelling(row.classification)}</Td>
                <Td>{fixSpelling(row.origin)}</Td>
                <Td title={row.history || ''}>{row.history && row.history.length > 15 ? `${row.history.substr(0, 15)}...` : (row.history || '-')}</Td>
                <Td>R$ {currencyFormat(Number(row.value.toFixed(2)))}</Td>
                <Td>{fixSpelling(row.status)}</Td>
                <Td>
                  <div className="icons">
                    <AiFillEye className="eye-icon" onClick={() => handleEditCashflowForm(row)} />
                    <FaTrash className="trash-icon" onClick={() => handleDelete(row.id, row.recurrenceId)} />
                  </div>
                </Td>
              </Tr>
            )) : (
              <Tr>
                <Td colSpan={11}>Nenhum lançamento encontrado</Td>
              </Tr>
            )}
          </CashflowTableInlineFilters>
        </div>
      </div>

      <ImagesList data={images} />
      <CashflowOptions open={cashflowOptions.open} onClose={handleCloseCashflowOptions} defaultStep={cashflowOptions.defaultStep} />
      {deleteRecurrenceModal.open && (
        <DeleteRecurrenceModal
          data={{ defaultSelected: deleteRecurrenceModal.defaultSelected, recurrenceId: deleteRecurrenceModal.recurrenceId }}
          onClose={closeDeleteRecurrenceModal}
          onSave={(ids) => handleDeleteRecurrence(ids)}
        />
      )}
    </div>
  );
}

export default Cashflow;
